/**
 * Stock insights: the Analytics panels added on 11 October 2026.
 *
 * These are NOT ported from the prototype. Its analytics block stops at the
 * headline stats, retail performance and a month comparison (`analytics.ts`).
 * Emmanuel asked for the page to be "comprehensive, exhaustive but useful", so
 * everything here is new behaviour with no oracle in `design/`; the tests in
 * `insights.test.ts` are the specification.
 *
 * ## Everything is derived from the ledger
 *
 * Same position as `analytics.ts`: no analytics table, no rollup. Each figure
 * is answered from movements, batches and products as they stand, so it cannot
 * drift from the records it describes.
 *
 * ## The caller sends only movements that still stand
 *
 * A void writes a reversing row and leaves the original in place. The API
 * drops both halves of every voided pair before calling in, so nothing here
 * has to reason about reversals: every line is something that really happened
 * and was not undone.
 *
 * ## Money is at cost unless it says otherwise
 *
 * "Where the stock money went" is about what stock cost, so used, sold and
 * written-off quantities are all valued at the batch's cost. Retail revenue is
 * the one figure at selling price, and it is named as revenue.
 */
import { addDays, addMonths, daysBetween, monthKey } from '../clock.js';
import { USAGE_PURPOSE_LABEL, WRITE_OFF_REASON_LABEL } from './schemas.js';
/** The periods the page offers. Anything else is refused at the API. */
export const INSIGHT_PERIODS = [30, 90, 365];
const LOSS_REASONS = new Set(['damaged', 'lost']);
export function flowOf(type, reason) {
    if (type === 'in') {
        if (reason === 'received')
            return 'bought';
        if (reason === 'import')
            return 'opening';
        if (reason === 'stock_count')
            return 'found';
        return 'other';
    }
    if (type === 'waste')
        return 'writtenOff';
    if (type === 'out') {
        if (reason === 'sold')
            return 'sold';
        if (reason === 'stock_count')
            return 'countLoss';
        // Logged through "Log use" as damaged or lost: a loss, not usage.
        if (reason !== null && LOSS_REASONS.has(reason))
            return 'writtenOff';
        return 'used';
    }
    return 'other';
}
/** How many products a hover box names before it says "and N more". */
export const SHARE_LIMIT = 4;
// ------------------------------------------------------------------ helpers
const MOST_USED_LIMIT = 6;
const BEST_SELLER_LIMIT = 5;
/** Days of cover at or under which a product needs ordering now. */
export const COVER_ORDER_NOW_DAYS = 14;
export const COVER_ORDER_SOON_DAYS = 30;
/** A suggested minimum holds this many days of use. */
const SUGGESTED_MIN_DAYS = 14;
/** The shortest stretch a usage rate is worked out over. */
const MIN_RATE_DAYS = 7;
function compare(pence, previousPence) {
    return {
        pence,
        previousPence,
        changePercent: previousPence === 0 ? null : Math.round(((pence - previousPence) / previousPence) * 100),
    };
}
/** 0 for Monday through 6 for Sunday. */
function weekdayIndex(date) {
    const [y, m, d] = date.split('-').map(Number);
    return (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7;
}
function firstOfMonth(date) {
    return `${date.slice(0, 7)}-01`;
}
const isConsumption = (k) => k === 'used' || k === 'sold';
/** Adds to one product's running share in a map keyed by product id. */
function addShare(into, productId, productName, units, pence) {
    const e = into.get(productId) ?? { productName, units: 0, pence: 0 };
    e.units += units;
    e.pence += pence;
    into.set(productId, e);
}
/** Largest value first, then by units, then by name so the order is stable. */
function ranked(shares) {
    return [...shares.values()].sort((a, b) => b.pence - a.pence || b.units - a.units || a.productName.localeCompare(b.productName));
}
function wasteLabel(reason) {
    return (WRITE_OFF_REASON_LABEL[reason] ??
        USAGE_PURPOSE_LABEL[reason] ??
        'Other');
}
// ---------------------------------------------------------------- the report
export function stockInsights(input) {
    const { today, days, ledger, products, lots } = input;
    const discards = input.discards ?? [];
    // The period is the last `days` days ending today; the one before it is the
    // same length, ending the day before the period starts.
    const periodStart = addDays(today, -(days - 1));
    const previousStart = addDays(periodStart, -days);
    const inPeriod = (d) => d >= periodStart && d <= today;
    const inPrevious = (d) => d >= previousStart && d < periodStart;
    // ------------------------------------------------------------- totals
    let bought = 0;
    let boughtPrev = 0;
    let usedSold = 0;
    let usedSoldPrev = 0;
    let writtenOff = 0;
    let writtenOffPrev = 0;
    const wasteBy = new Map();
    const wasteByProduct = new Map();
    const unitsBy = new Map();
    const weekdays = [0, 0, 0, 0, 0, 0, 0];
    const soldBy = new Map();
    const retail = { revenuePence: 0, costPence: 0, units: 0 };
    const counts = {
        lastOn: null,
        shortUnits: 0,
        shortPence: 0,
        foundUnits: 0,
        foundPence: 0,
        netPence: 0,
    };
    const countShort = new Map();
    const countFound = new Map();
    for (const line of ledger) {
        const kind = flowOf(line.type, line.reason);
        const cost = line.qty * line.unitCostPence;
        const now = inPeriod(line.occurredOn);
        const before = !now && inPrevious(line.occurredOn);
        if (!now && !before)
            continue;
        if (kind === 'bought') {
            if (now)
                bought += cost;
            else
                boughtPrev += cost;
        }
        else if (isConsumption(kind)) {
            if (now)
                usedSold += cost;
            else
                usedSoldPrev += cost;
        }
        else if (kind === 'writtenOff') {
            if (now)
                writtenOff += cost;
            else
                writtenOffPrev += cost;
        }
        if (kind === 'sold') {
            const e = soldBy.get(line.productId) ?? {
                productName: line.productName,
                units: 0,
                revenuePence: 0,
                previousRevenuePence: 0,
            };
            const revenue = line.qty * (line.pricePence ?? 0);
            if (now) {
                e.units += line.qty;
                e.revenuePence += revenue;
                retail.revenuePence += revenue;
                retail.costPence += cost;
                retail.units += line.qty;
            }
            else {
                e.previousRevenuePence += revenue;
            }
            soldBy.set(line.productId, e);
        }
        if (!now)
            continue;
        if (isConsumption(kind)) {
            const e = unitsBy.get(line.productId) ?? { productName: line.productName, units: 0 };
            e.units += line.qty;
            unitsBy.set(line.productId, e);
            weekdays[weekdayIndex(line.occurredOn)] += line.qty;
        }
        if (kind === 'writtenOff') {
            const reason = line.reason ?? 'other';
            const r = wasteBy.get(reason) ?? { pence: 0, units: 0 };
            r.pence += cost;
            r.units += line.qty;
            wasteBy.set(reason, r);
            const p = wasteByProduct.get(line.productId) ?? { productName: line.productName, pence: 0 };
            p.pence += cost;
            wasteByProduct.set(line.productId, p);
        }
        if (kind === 'countLoss' || kind === 'found') {
            if (counts.lastOn === null || line.occurredOn > counts.lastOn) {
                counts.lastOn = line.occurredOn;
            }
            if (kind === 'countLoss') {
                counts.shortUnits += line.qty;
                counts.shortPence += cost;
                addShare(countShort, line.productId, line.productName, line.qty, cost);
            }
            else {
                counts.foundUnits += line.qty;
                counts.foundPence += cost;
                addShare(countFound, line.productId, line.productName, line.qty, cost);
            }
        }
    }
    counts.netPence = counts.foundPence - counts.shortPence;
    // ------------------------------------------------------------- months
    const monthCount = days === 365 ? 12 : 6;
    const thisMonth = firstOfMonth(today);
    const monthKeys = [];
    for (let i = monthCount - 1; i >= 0; i--)
        monthKeys.push(monthKey(addMonths(thisMonth, -i)));
    const monthIndex = new Map(monthKeys.map((k, i) => [k, i]));
    const months = monthKeys.map((k) => ({
        monthKey: k,
        boughtPence: 0,
        usedPence: 0,
        soldPence: 0,
        writtenOffPence: 0,
        stockValuePence: 0,
        products: { bought: [], used: [], sold: [], writtenOff: [] },
    }));
    const monthShares = monthKeys.map(() => ({
        bought: new Map(),
        used: new Map(),
        sold: new Map(),
        writtenOff: new Map(),
    }));
    // Net change in stock value in each month, from every movement that touches
    // the shelf, so the value at each month's end can be walked back from today.
    const netByMonth = new Array(monthCount).fill(0);
    for (const line of ledger) {
        const i = monthIndex.get(monthKey(line.occurredOn));
        if (i === undefined || line.occurredOn > today)
            continue;
        const kind = flowOf(line.type, line.reason);
        const cost = line.qty * line.unitCostPence;
        const month = months[i];
        if (kind === 'bought')
            month.boughtPence += cost;
        else if (kind === 'used')
            month.usedPence += cost;
        else if (kind === 'sold')
            month.soldPence += cost;
        else if (kind === 'writtenOff')
            month.writtenOffPence += cost;
        if (kind === 'bought' || kind === 'used' || kind === 'sold' || kind === 'writtenOff') {
            addShare(monthShares[i][kind], line.productId, line.productName, line.qty, cost);
        }
        netByMonth[i] += line.type === 'in' ? cost : -cost;
    }
    months.forEach((month, i) => {
        for (const kind of ['bought', 'used', 'sold', 'writtenOff']) {
            month.products[kind] = ranked(monthShares[i][kind]);
        }
    });
    let value = products.reduce((sum, p) => sum + p.stockValuePence, 0);
    for (let i = monthCount - 1; i >= 0; i--) {
        months[i].stockValuePence = Math.max(0, value);
        value -= netByMonth[i];
    }
    // -------------------------------------------------------------- expiry
    const expiryKeys = [];
    for (let i = 0; i < 6; i++)
        expiryKeys.push(monthKey(addMonths(thisMonth, i)));
    const expiryIndex = new Map(expiryKeys.map((k, i) => [k, i]));
    const expiryMonths = expiryKeys.map((k) => ({
        monthKey: k,
        valuePence: 0,
        products: [],
    }));
    const expiryShares = expiryKeys.map(() => new Map());
    let expiredPence = 0;
    for (const lot of lots) {
        if (lot.expiry < today) {
            expiredPence += lot.valuePence;
            continue;
        }
        const i = expiryIndex.get(monthKey(lot.expiry));
        if (i === undefined)
            continue;
        expiryMonths[i].valuePence += lot.valuePence;
        if (lot.productName) {
            // Keyed by name: two batches of one product in a month are one line.
            addShare(expiryShares[i], lot.productName, lot.productName, lot.units ?? 0, lot.valuePence);
        }
    }
    expiryMonths.forEach((month, i) => {
        month.products = ranked(expiryShares[i]);
    });
    // -------------------------------------------------- open-pack discards
    for (const d of discards) {
        if (d.occurredOn > today)
            continue;
        const now = inPeriod(d.occurredOn);
        const before = !now && inPrevious(d.occurredOn);
        if (now || before) {
            if (now) {
                writtenOff += d.valuePence;
                usedSold = Math.max(0, usedSold - d.valuePence);
                const r = wasteBy.get(d.reason) ?? { pence: 0, units: 0 };
                r.pence += d.valuePence;
                wasteBy.set(d.reason, r);
                const p = wasteByProduct.get(d.productId) ?? { productName: d.productName, pence: 0 };
                p.pence += d.valuePence;
                wasteByProduct.set(d.productId, p);
            }
            else {
                writtenOffPrev += d.valuePence;
                usedSoldPrev = Math.max(0, usedSoldPrev - d.valuePence);
            }
        }
        const i = monthIndex.get(monthKey(d.occurredOn));
        if (i !== undefined) {
            const month = months[i];
            month.writtenOffPence += d.valuePence;
            month.usedPence = Math.max(0, month.usedPence - d.valuePence);
            addShare(monthShares[i].writtenOff, d.productId, d.productName, 0, d.valuePence);
        }
    }
    months.forEach((month, i) => {
        month.products.writtenOff = ranked(monthShares[i].writtenOff);
    });
    // --------------------------------------------------------------- waste
    const byReason = [...wasteBy.entries()]
        .map(([reason, v]) => ({ reason, label: wasteLabel(reason), ...v }))
        .sort((a, b) => b.pence - a.pence || a.label.localeCompare(b.label));
    const topWaste = [...wasteByProduct.values()].sort((a, b) => b.pence - a.pence || a.productName.localeCompare(b.productName))[0];
    // --------------------------------------------------------------- usage
    const mostUsed = [...unitsBy.entries()]
        .map(([productId, v]) => ({ productId, ...v }))
        .sort((a, b) => b.units - a.units || a.productName.localeCompare(b.productName))
        .slice(0, MOST_USED_LIMIT);
    const peak = Math.max(...weekdays);
    const busiestWeekday = peak === 0 ? null : weekdays.indexOf(peak);
    // ------------------------------------------------------- sitting still
    // On the shelf for the whole period with nothing used or sold in it. Stock
    // that arrived inside the period has not had the chance to sit still.
    const still = products
        .filter((p) => p.qtyOnHand > 0 &&
        p.firstStockedOn !== null &&
        p.firstStockedOn < periodStart &&
        (p.lastMovedOn === null || p.lastMovedOn < periodStart))
        .map((p) => ({
        productId: p.productId,
        productName: p.productName,
        qtyOnHand: p.qtyOnHand,
        valuePence: p.stockValuePence,
        lastMovedOn: p.lastMovedOn,
    }))
        .sort((a, b) => b.valuePence - a.valuePence || a.productName.localeCompare(b.productName));
    // --------------------------------------------------------------- cover
    const order = {
        out: 0,
        order_now: 1,
        order_soon: 2,
        ok: 3,
        not_moving: 4,
    };
    const cover = [];
    for (const p of products) {
        const units = unitsBy.get(p.productId)?.units ?? 0;
        if (units === 0 && p.qtyOnHand === 0)
            continue;
        // A product stocked for the first time a fortnight ago was not being used
        // for the 76 days before that; dividing by 90 would understate its rate.
        const known = p.firstStockedOn === null ? days : Math.min(days, daysBetween(p.firstStockedOn, today) + 1);
        const rateDays = Math.max(MIN_RATE_DAYS, known);
        const perDay = units / rateDays;
        if (units === 0) {
            cover.push({
                productId: p.productId,
                productName: p.productName,
                qtyOnHand: p.qtyOnHand,
                minLevel: p.minLevel,
                perWeek: 0,
                daysOfCover: null,
                runsOutOn: null,
                status: 'not_moving',
                suggestedMinLevel: null,
            });
            continue;
        }
        const daysOfCover = Math.floor(p.qtyOnHand / perDay);
        const status = p.qtyOnHand === 0
            ? 'out'
            : daysOfCover <= COVER_ORDER_NOW_DAYS
                ? 'order_now'
                : daysOfCover <= COVER_ORDER_SOON_DAYS
                    ? 'order_soon'
                    : 'ok';
        cover.push({
            productId: p.productId,
            productName: p.productName,
            qtyOnHand: p.qtyOnHand,
            minLevel: p.minLevel,
            perWeek: Math.round(perDay * 7 * 10) / 10,
            daysOfCover,
            runsOutOn: p.qtyOnHand === 0 ? null : addDays(today, daysOfCover),
            status,
            suggestedMinLevel: Math.max(1, Math.ceil(perDay * SUGGESTED_MIN_DAYS)),
        });
    }
    cover.sort((a, b) => order[a.status] - order[b.status] ||
        (a.daysOfCover ?? Infinity) - (b.daysOfCover ?? Infinity) ||
        a.productName.localeCompare(b.productName));
    // -------------------------------------------------------------- retail
    const retailBucket = days === 365 ? 'month' : 'week';
    let retailSeries;
    if (retailBucket === 'month') {
        retailSeries = monthKeys.map((k) => ({ from: `${k}-01`, revenuePence: 0 }));
        for (const line of ledger) {
            if (flowOf(line.type, line.reason) !== 'sold' || line.occurredOn > today)
                continue;
            const i = monthIndex.get(monthKey(line.occurredOn));
            if (i !== undefined)
                retailSeries[i].revenuePence += line.qty * (line.pricePence ?? 0);
        }
    }
    else {
        const buckets = Math.ceil(days / 7);
        retailSeries = [];
        for (let i = 0; i < buckets; i++) {
            retailSeries.push({ from: addDays(periodStart, i * 7), revenuePence: 0 });
        }
        for (const line of ledger) {
            if (flowOf(line.type, line.reason) !== 'sold' || !inPeriod(line.occurredOn))
                continue;
            const i = Math.floor(daysBetween(periodStart, line.occurredOn) / 7);
            retailSeries[i].revenuePence += line.qty * (line.pricePence ?? 0);
        }
    }
    const bestSellers = [...soldBy.entries()]
        .filter(([, v]) => v.units > 0)
        .map(([productId, v]) => ({
        productId,
        productName: v.productName,
        units: v.units,
        revenuePence: v.revenuePence,
        previousRevenuePence: v.previousRevenuePence,
        changePercent: compare(v.revenuePence, v.previousRevenuePence).changePercent,
    }))
        .sort((a, b) => b.revenuePence - a.revenuePence || a.productName.localeCompare(b.productName))
        .slice(0, BEST_SELLER_LIMIT);
    return {
        days,
        totals: {
            bought: compare(bought, boughtPrev),
            usedSold: compare(usedSold, usedSoldPrev),
            writtenOff: compare(writtenOff, writtenOffPrev),
            wasteRatePercent: bought === 0 ? null : Math.round((writtenOff / bought) * 1000) / 10,
        },
        months,
        expiry: { expiredPence, months: expiryMonths },
        waste: {
            byReason,
            top: topWaste && topWaste.pence > 0 ? topWaste : null,
        },
        mostUsed,
        weekdays,
        busiestWeekday,
        sittingStill: {
            totalPence: still.reduce((sum, p) => sum + p.valuePence, 0),
            products: still,
        },
        cover,
        retail: {
            revenuePence: retail.revenuePence,
            profitPence: retail.revenuePence - retail.costPence,
            marginPercent: retail.revenuePence === 0
                ? null
                : Math.round(((retail.revenuePence - retail.costPence) / retail.revenuePence) * 100),
            units: retail.units,
        },
        retailBucket,
        retailSeries,
        bestSellers,
        counts: {
            ...counts,
            items: [
                ...ranked(countShort).map((x) => ({ ...x, kind: 'short' })),
                ...ranked(countFound).map((x) => ({ ...x, kind: 'found' })),
            ].sort((a, b) => b.pence - a.pence || a.productName.localeCompare(b.productName)),
        },
    };
}
/**
 * The earliest date the report reads. The API uses it to bound its ledger
 * query: far enough back for the previous period and for every month drawn.
 */
export function insightsLedgerStart(today, days) {
    const previousStart = addDays(today, -(days * 2 - 1));
    const firstMonth = addMonths(firstOfMonth(today), -((days === 365 ? 12 : 6) - 1));
    return previousStart < firstMonth ? previousStart : firstMonth;
}
//# sourceMappingURL=insights.js.map